import { Box, FormControl, Grid, IconButton, Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import useUIFlowTabComponent from 'src/@core/hooks/useUIFlowTabComponent';
import VigoDataGrid from 'src/@vigo/vigo-datagrid';
import { DataGridColumn, MultiSelectButtonDef } from 'src/@vigo/vigo-datagrid/datagrid-types';
import VigoSpinner from 'src/@vigo/vigo-spinner';
import { VigoTextInput } from 'src/@vigo/vigo-textinput';
import { VigoTypography } from 'src/@vigo/vigo-typography';
import { CustomerProfilesDto, GeneralConfigProfileModel } from 'src/models/relationshipmanagement';
import { useAppDispatch } from 'src/store/baseHooks';
import * as yup from 'yup';
import { ConsignmentSettingsGroupDetails, DefaultsState } from './types';
import { useStateWithRef } from 'src/@core/hooks/useStateWithRef';
import { useConsignmentSettingsGroupDetailsQuery } from './queries';
import { setActiveTabDirtyProperty, setItemHeaderTitle } from 'src/store/slices/uiFlowSlice';
import { DeleteOutline } from 'mdi-material-ui';
import DefaultGridState from './gridLayouts/assignedCustomersLayout.json';
import { AssignedCustomersState } from '../util/types';
import { useConsignmentSettingsGroupMutations } from './mutations';
import { useAuth } from 'react-oidc-context';
import { VigoConfigToggle } from 'src/@vigo/vigo-configtoggle';
import { useAdditionalDataPanel } from 'src/@core/hooks/useAdditionalDataPanel';
import useImpersonateSessionData from 'src/@core/hooks/useImpersonateSessionData';

const schemaResolver = yup.object({
  name: yup.string().required('Name is required'),
  description: yup.string().required('Description is required'),
  restrictReferenceCharacters: yup.boolean(),
  invalidReferenceCharacters: yup.string().when('restrictReferenceCharacters', {
    is: true,
    then: yup.string().min(1, 'At least one character must be specified').required('Invalid Characters is required')
  }),
  dashboardId: yup.string().nullable()
});

export const defaultEtaConfig = {
  etaDisplayHighThreshold: '17:00',
  etaDisplayLowThreshold: '09:00',
  etaDisplayRangeUnit: '0', // Hours
  etaDisplayRangeValue: '1', // 1
  etaDisplaySetThreshold: false,
  etaDisplayType: '2', // Display ETA Range
  etaRangeRoundingType: '2', // To the nearest 10 minutes
  exampleEtaValue: '10:33'
};

const defaultFormData: GeneralConfigProfileModel = {
  allowMultipleOstypes: true,
  description: '',
  name: '',
  activateCollectionTime: false,
  allowEditingColNote1: false,
  allowEditingColNote2: false,
  allowEditingColNote3: false,
  allowEditingColNote4: false,
  allowEditingDelNote1: false,
  allowEditingDelNote2: false,
  allowEditingDelNote3: false,
  allowEditingDelNote4: false,
  allowManualAddressEntry: false,
  allowTotalSpacesEdit: false,
  automaticSalesOrderNumbering: false,
  customerPaperworkAllowEditAfterCollectionDate: false,
  customerPaperworkAllowEditAfterCutOffTime: false,
  customerPaperworkAllowed: false,
  customerPaperworkMandatory: false,
  dashboardId: '',
  enforceCollectionContactName: false,
  enforceCollectionContactPhone: false,
  enforceDeliveryContactName: false,
  enforceDeliveryContactPhone: false,
  etaDisplayHighThreshold: defaultEtaConfig.etaDisplayHighThreshold,
  etaDisplayLowThreshold: defaultEtaConfig.etaDisplayLowThreshold,
  etaDisplayRangeUnit: parseInt(defaultEtaConfig.etaDisplayRangeUnit),
  etaDisplayRangeValue: parseInt(defaultEtaConfig.etaDisplayRangeValue),
  etaDisplaySetThreshold: defaultEtaConfig.etaDisplaySetThreshold,
  etaDisplayType: parseInt(defaultEtaConfig.etaDisplayType),
  etaRangeRoundingType: parseInt(defaultEtaConfig.etaRangeRoundingType),
  invalidReferenceCharacters: '',
  isActive: false,
  isDefault: false,
  restrictReferenceCharacters: false,
  selectLabelOnPrint: false,
  version: ''
};

interface GeneralConfigProfileProps {
  id: string;
}

const GeneralConfigProfile = ({ id }: GeneralConfigProfileProps) => {
  // State
  const [consignmentSettingsGroupDetails, setConsignmentSettingsGroupDetails] =
    useState<ConsignmentSettingsGroupDetails>();
  const [assignedCustomers, setAssignedCustomers, assignedCustomersRef] = useStateWithRef<AssignedCustomersState[]>([]);
  const [, setFilteredCustomers, filteredCustomersRef] = useStateWithRef<AssignedCustomersState[]>([]);
  const [rowIds, setRowIds] = useState<string[]>([]);

  // Hooks
  const dispatch = useAppDispatch();
  const { control, methods, addRecentHistory, reset, set, setFormValue } = useUIFlowTabComponent({
    defaultFormData: defaultFormData,
    schemaResolver: schemaResolver,
    activeTabFunctions: { save }
  });
  const queryClient = useQueryClient();
  const { addAdditionalDataPanel, removeAdditionalDataPanel, setSidePanelClosed } = useAdditionalDataPanel();
  const { isImpersonating, impersonateData } = useImpersonateSessionData();

  // Watchers
  const isActive = methods.watch('isActive') as boolean;
  const isDefault = methods.watch('isDefault') as boolean;

  const auth = useAuth();
  const tenantId = useMemo(() => {
    if (isImpersonating) {
      return impersonateData?.tenantId ?? '';
    }

    return (auth?.user?.profile?.tenantId as string) ?? '';
  }, [auth?.user?.profile?.tenantId, impersonateData?.tenantId, isImpersonating]);
  const disableIsActive = useMemo(() => {
    return isActive && assignedCustomers.length > 0;
  }, [isActive, assignedCustomers]);

  const setMultiSelectRowIds = (data: any[]) => {
    setRowIds(data);
  };

  const consignmentSettingsGroupDetailsOnSuccess = (res: ConsignmentSettingsGroupDetails) => {
    if (res) {
      setConsignmentSettingsGroupDetails(res);
      const assignedCustomersRows: AssignedCustomersState[] = [];
      if (res.assignedRelationships) {
        for (let index = 0; index < res.assignedRelationships.length; index++) {
          const customer = res.assignedRelationships[index];

          assignedCustomersRows.push({
            id: customer.id ?? '',
            accountCode: customer.accountCode ?? '',
            name: customer.name ?? ''
          });
        }
      }
      setAssignedCustomers(assignedCustomersRows);
      setFilteredCustomers(assignedCustomersRows);

      set('consignmentSettingsGroupInfo', {
        customers: res.allRelationships ?? [],
        printingTypes: res.printingTypes ?? [],
        handleRowAdd: handleRowAdd,
        handleBulkRowAdd: handleBulkRowAdd,
        defaultEtaConfig: defaultEtaConfig,
        labels: res.labels ?? []
      });

      if (id) {
        addRecentHistory('Edit', res.consignmentSettingsGroup?.name ?? '');

        const printingDefaultsState: DefaultsState[] = [];
        if (res.printingDefaults) {
          for (let index = 0; index < res.printingDefaults.length; index++) {
            printingDefaultsState.push({
              id: res.printingDefaults[index].id ?? '',
              printAsDefault: res.printingDefaults[index].printAsDefault ?? false,
              printingTypeId: res.printingDefaults[index].printingTypeId ?? '',
              quantity: res.printingDefaults[index].quantity ?? 0,
              networkId: res.printingDefaults[index].networkId ?? '',
              labelId: res.printingDefaults[index].labelId ?? '',
              version: res.printingDefaults[index].version ?? ''
            });
          }
        }

        reset({
          allowMultipleOstypes:
            res.consignmentSettingsGroup?.allowMultipleOstypes ?? defaultFormData.allowMultipleOstypes,
          description: res.consignmentSettingsGroup?.description ?? defaultFormData.description,
          name: res.consignmentSettingsGroup?.name ?? defaultFormData.name,
          activateCollectionTime:
            res.consignmentSettingsGroup?.activateCollectionTime ?? defaultFormData.activateCollectionTime,
          allowEditingColNote1:
            res.consignmentSettingsGroup?.allowEditingColNote1 ?? defaultFormData.allowEditingColNote1,
          allowEditingColNote2:
            res.consignmentSettingsGroup?.allowEditingColNote2 ?? defaultFormData.allowEditingColNote2,
          allowEditingColNote3:
            res.consignmentSettingsGroup?.allowEditingColNote3 ?? defaultFormData.allowEditingColNote3,
          allowEditingColNote4:
            res.consignmentSettingsGroup?.allowEditingColNote4 ?? defaultFormData.allowEditingColNote4,
          allowEditingDelNote1:
            res.consignmentSettingsGroup?.allowEditingDelNote1 ?? defaultFormData.allowEditingDelNote1,
          allowEditingDelNote2:
            res.consignmentSettingsGroup?.allowEditingDelNote2 ?? defaultFormData.allowEditingDelNote2,
          allowEditingDelNote3:
            res.consignmentSettingsGroup?.allowEditingDelNote3 ?? defaultFormData.allowEditingDelNote3,
          allowEditingDelNote4:
            res.consignmentSettingsGroup?.allowEditingDelNote4 ?? defaultFormData.allowEditingDelNote4,
          allowManualAddressEntry:
            res.consignmentSettingsGroup?.allowManualAddressEntry ?? defaultFormData.allowManualAddressEntry,
          allowTotalSpacesEdit:
            res.consignmentSettingsGroup?.allowTotalSpacesEdit ?? defaultFormData.allowTotalSpacesEdit,
          automaticSalesOrderNumbering:
            res.consignmentSettingsGroup?.automaticSalesOrderNumbering ?? defaultFormData.automaticSalesOrderNumbering,
          customerPaperworkAllowEditAfterCollectionDate:
            res.consignmentSettingsGroup?.customerPaperworkAllowEditAfterCollectionDate ??
            defaultFormData.customerPaperworkAllowEditAfterCollectionDate,
          customerPaperworkAllowEditAfterCutOffTime:
            res.consignmentSettingsGroup?.customerPaperworkAllowEditAfterCutOffTime ??
            defaultFormData.customerPaperworkAllowEditAfterCutOffTime,
          customerPaperworkAllowed:
            res.consignmentSettingsGroup?.customerPaperworkAllowed ?? defaultFormData.customerPaperworkAllowed,
          customerPaperworkMandatory:
            res.consignmentSettingsGroup?.customerPaperworkMandatory ?? defaultFormData.customerPaperworkMandatory,
          dashboardId: res.consignmentSettingsGroup?.dashboardId ?? defaultFormData.dashboardId,
          enforceCollectionContactName:
            res.consignmentSettingsGroup?.enforceCollectionContactName ?? defaultFormData.enforceCollectionContactName,
          enforceCollectionContactPhone:
            res.consignmentSettingsGroup?.enforceCollectionContactPhone ??
            defaultFormData.enforceCollectionContactPhone,
          enforceDeliveryContactName:
            res.consignmentSettingsGroup?.enforceDeliveryContactName ?? defaultFormData.enforceDeliveryContactName,
          enforceDeliveryContactPhone:
            res.consignmentSettingsGroup?.enforceDeliveryContactPhone ?? defaultFormData.enforceDeliveryContactPhone,
          etaDisplayHighThreshold:
            res.consignmentSettingsGroup?.etaDisplayHighThreshold ?? defaultFormData.etaDisplayHighThreshold,
          etaDisplayLowThreshold:
            res.consignmentSettingsGroup?.etaDisplayLowThreshold ?? defaultFormData.etaDisplayLowThreshold,
          etaDisplayRangeUnit: res.consignmentSettingsGroup?.etaDisplayRangeUnit ?? defaultFormData.etaDisplayRangeUnit,
          etaDisplayRangeValue:
            res.consignmentSettingsGroup?.etaDisplayRangeValue ?? defaultFormData.etaDisplayRangeValue,
          etaDisplaySetThreshold:
            res.consignmentSettingsGroup?.etaDisplaySetThreshold ?? defaultFormData.etaDisplaySetThreshold,
          etaDisplayType: res.consignmentSettingsGroup?.etaDisplayType ?? defaultFormData.etaDisplayType,
          etaRangeRoundingType:
            res.consignmentSettingsGroup?.etaRangeRoundingType ?? defaultFormData.etaRangeRoundingType,
          invalidReferenceCharacters:
            res.consignmentSettingsGroup?.invalidReferenceCharacters ?? defaultFormData.invalidReferenceCharacters,
          isActive: res.consignmentSettingsGroup?.isActive ?? defaultFormData.isActive,
          isDefault: res.consignmentSettingsGroup?.isDefault ?? defaultFormData.isDefault,
          printingDefaults: printingDefaultsState,
          restrictReferenceCharacters:
            res.consignmentSettingsGroup?.restrictReferenceCharacters ?? defaultFormData.restrictReferenceCharacters,
          selectLabelOnPrint:
            res.consignmentSettingsGroup?.selectLabelOnPrint ?? defaultFormData.selectLabelOnPrint,
          version: res.consignmentSettingsGroup?.version ?? defaultFormData.version
        });

        dispatch(setItemHeaderTitle(res.consignmentSettingsGroup?.name ?? defaultFormData.name));
      }

      setFormValue('assignedCustomers', assignedCustomersRows);
    }
  };

  /** REACT QUERY - QUERIES */

  const { isFetching: detailsInfoFetching, refetch } = useConsignmentSettingsGroupDetailsQuery(id, tenantId, {
    labelParams: {},
    onSuccess: consignmentSettingsGroupDetailsOnSuccess
  });

  /** END OF REACT QUERY */

  // MUTATIONS
  const consignmentSettingsGroupMutations = useConsignmentSettingsGroupMutations(
    id,
    consignmentSettingsGroupDetails,
    assignedCustomersRef,
    addRecentHistory
  );
  const { saveMutation } = consignmentSettingsGroupMutations;

  const handleRowDelete = (id: string) => {
    dispatch(setActiveTabDirtyProperty(true));

    const existingRows = [...assignedCustomersRef.current];
    const row = existingRows.findIndex(x => x.id == id);

    existingRows.splice(row, 1);
    setAssignedCustomers(existingRows);
    setFilteredCustomers(existingRows);

    setFormValue('assignedCustomers', existingRows);
  };

  const handleRowAdd = (row: CustomerProfilesDto) => {
    dispatch(setActiveTabDirtyProperty(true));

    const existingRows = [...assignedCustomersRef.current];

    if (existingRows.find(x => x.id === row.id)) return; // Row already exists in table

    const newRow = {
      id: row.id ?? '',
      accountCode: row.accountCode ?? '',
      name: row.name ?? ''
    };

    existingRows.push(newRow);
    setAssignedCustomers(existingRows);
    setFilteredCustomers(existingRows);

    setFormValue('assignedCustomers', existingRows);
  };

  const handleBulkRowAdd = (rows: CustomerProfilesDto[]) => {
    dispatch(setActiveTabDirtyProperty(true));

    const existingRows = [...assignedCustomersRef.current];

    for (let index = 0; index < rows.length; index++) {
      if (existingRows.find(x => x.id === rows[index].id)) continue; // Row already exists in table

      const newRow = {
        id: rows[index].id ?? '',
        accountCode: rows[index].accountCode ?? '',
        name: rows[index].name ?? ''
      };

      existingRows.push(newRow);
    }

    setAssignedCustomers(existingRows);
    setFilteredCustomers(existingRows);

    setFormValue('assignedCustomers', existingRows);
  };

  function handleSearch(text: string) {
    if (text === '') {
      setFilteredCustomers(assignedCustomersRef.current);

      return;
    }

    const filteredRows: AssignedCustomersState[] = [];

    for (let filteredIndex = 0; filteredIndex < assignedCustomersRef.current.length; filteredIndex++) {
      const row = assignedCustomersRef.current[filteredIndex];

      if (row.accountCode?.toLowerCase().includes(text.toLowerCase())) {
        filteredRows.push(row);
      }

      if (row.name?.toLowerCase().includes(text.toLowerCase()) && !filteredRows.includes(row)) {
        filteredRows.push(row);
      }
    }

    setFilteredCustomers(filteredRows);
  }

  function save(onSuccess?: () => void | undefined): any {
    methods.handleSubmit(async (formData: any) => {
      saveMutation.mutateAsync(
        {
          formData: formData,
          id: id
        },
        {
          onSuccess: () => {
            if (typeof onSuccess === 'function') onSuccess();

            dispatch(setActiveTabDirtyProperty(false));

            refetch();
          }
        }
      );
    })();
  }

  const handleIsActivePress = useCallback(
    (newValue: boolean) => {
      if (isDefault && !newValue) {
        setFormValue('isDefault', false);
      }
    },
    [isDefault, setFormValue]
  );

  const handleIsDefaultPress = useCallback(
    (newValue: boolean) => {
      if (newValue && !isActive) {
        setFormValue('isActive', true);
      }
    },
    [isActive, setFormValue]
  );

  const deleteButton = useMemo(
    () => (
      <IconButton
        onClick={() => {
          const existingRows = [...assignedCustomersRef.current];
          for (let index = 0; index < rowIds.length; index++) {
            const rowIndex = existingRows.findIndex(x => x.id == rowIds[index]);

            existingRows.splice(rowIndex, 1);
          }
          setAssignedCustomers(existingRows);
          setFilteredCustomers(existingRows);

          setFormValue('assignedCustomers', existingRows);
        }}
        sx={{ position: 'absolute', right: '15px', top: '-25px' }}>
        <DeleteOutline />
      </IconButton>
    ),
    [assignedCustomersRef, rowIds, setAssignedCustomers, setFilteredCustomers, setFormValue]
  );

  const deleteButtonDef: MultiSelectButtonDef = useMemo(
    () => ({
      name: 'deleteButton',
      component: deleteButton,
      alwaysShow: false,
      showWithNoSelection: false
    }),
    [deleteButton]
  );

  const columns: DataGridColumn[] = [
    {
      field: 'accountCode',
      headerName: 'Acc No',
      width: 150,
      maxWidth: 200,
      hideable: false
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 300,
      maxWidth: 700
    }
  ];

  useEffect(() => {
    addAdditionalDataPanel({
      id: 'ConsignmentSettingsGroupPanel',
      name: 'consignmentsettingsgrouppanel',
      componentName: 'ConsignmentSettingsGroupPanel',
      open: true,
      fixed: true
    });

    return () => {
      removeAdditionalDataPanel('ConsignmentSettingsGroupPanel');
      setSidePanelClosed(true);
    };
  }, [addAdditionalDataPanel, removeAdditionalDataPanel, setSidePanelClosed]);

  useEffect(() => {
    return () => {
      queryClient.resetQueries(['generalConfigProfile-relationshipsData', { id }]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    return () => {
      methods.reset({});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (detailsInfoFetching) {
    return (
      <Box sx={{ width: '100%', height: '70vh', display: 'grid', placeContent: 'center' }}>
        <VigoSpinner name="generalConfigProfileSpinner" />
      </Box>
    );
  }

  return (
    <Stack direction="column" p={2}>
      <form onSubmit={e => e.preventDefault()}>
        <Grid container item direction="column" spacing={5}>
          <Grid item xs={12}>
            {/* HEADER SECTION */}
            <Stack direction="row">
              <Grid item xs={11}>
                <VigoTypography value="Consignment Settings Group" sx={{ fontWeight: 'bold' }} />
                <VigoTypography value="Enter a descriptive name for this Consignment Settings Group." sx={{ mt: 1 }} />
              </Grid>
              <Grid item>
                <VigoConfigToggle
                  formName="isActive"
                  label="Group Activation"
                  handleChange={handleIsActivePress}
                  tooltipText="Customers must be un-assigned before this group can be disabled"
                  shouldDisable={disableIsActive}
                  shouldShowTooltip={disableIsActive}
                />
              </Grid>
            </Stack>

            {/* FORM FIELDS */}
            <Stack direction="column" gap={4} pt="20px">
              <Stack direction="row">
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <Controller
                      name="name"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <VigoTextInput
                          {...field}
                          required
                          label="Consignment Settings Group Name"
                          value={field.value || ''}
                          error={!!error}
                          errormessage={error?.message}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={5} />
                <Grid item>
                  <VigoConfigToggle formName="isDefault" label="Default Group" handleChange={handleIsDefaultPress} />
                </Grid>
              </Stack>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <VigoTextInput
                        {...field}
                        required
                        label="Consignment Settings Group Description"
                        value={field.value || ''}
                        error={!!error}
                        errormessage={error?.message}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </Stack>

            {/* ASSIGNED CUSTOMERS */}
            <Stack direction="column" pt="20px">
              <VigoTypography value="Assigned Customers" sx={{ fontWeight: 'bold' }} />
              <VigoDataGrid
                disableVirtualization={false}
                name="generalConfigCustomersAssignedList"
                editServiceLocation="relationshipmanagement"
                editServiceEndpoint="Relationship"
                totalRows={filteredCustomersRef.current.length}
                currentLoadedRows={filteredCustomersRef.current.length}
                filterColumn={`generalConfigProfileId eq guid'${id}'`}
                sort="accountCode"
                isEditable={false}
                columns={columns}
                dataSource={filteredCustomersRef.current}
                sortingMode="client"
                defaultActions={{ canDelete: true, deleteCallBack: handleRowDelete }}
                disableExport
                disableFilters
                disablePrint
                disableImport
                customActions={[]}
                density="standard"
                initialState={{ pinnedColumns: { right: ['actions'] } }}
                hideFooter
                multiSelect
                setSelectedRowIds={setMultiSelectRowIds}
                multiSelectButtons={[deleteButtonDef]}
                sx={{ maxHeight: '59vh' }}
                defaultGridState={DefaultGridState}
                disableRowSelectionOnClick
                searchOverride={handleSearch}
              />
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Stack>
  );
};

export default GeneralConfigProfile;
